/**
 * Quality Indicator Component
 *
 * Shows the quality assessment score with visual feedback
 */

import { cn } from '@/lib/utils'
import { QUALITY_CONFIG, type AnswerQuality } from '@/lib/ai/conversationAI'

interface QualityIndicatorProps {
  score: AnswerQuality | null
  confidence?: number
  size?: 'sm' | 'md' | 'lg'
  showLabel?: boolean
  className?: string
}

export function QualityIndicator({
  score,
  confidence,
  size = 'md',
  showLabel = true,
  className,
}: QualityIndicatorProps) {
  if (!score) return null

  const config = QUALITY_CONFIG[score]

  const sizeClasses = {
    sm: 'h-1.5 text-xs',
    md: 'h-2 text-sm',
    lg: 'h-3 text-base',
  }

  const actualConfidence = confidence ?? config.confidence

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Progress bar */}
      <div className={cn('flex-1 bg-warm-200 rounded-full overflow-hidden', sizeClasses[size])}>
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{
            width: `${actualConfidence}%`,
            backgroundColor: config.color,
          }}
        />
      </div>

      {/* Label */}
      {showLabel && (
        <span
          className={cn('font-medium', sizeClasses[size])}
          style={{ color: config.color }}
        >
          {config.label}
        </span>
      )}
    </div>
  )
}

interface QualityBadgeProps {
  score: AnswerQuality
  className?: string
}

export function QualityBadge({ score, className }: QualityBadgeProps) {
  const config = QUALITY_CONFIG[score]

  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
        className
      )}
      style={{
        backgroundColor: `${config.color}20`,
        color: config.color,
      }}
    >
      {config.label}
    </span>
  )
}
